<template>
  <div style="overflow: hidden; position: relative" :style="styleObject">
    <attachment-thumbnail-progress
      v-if="isProgress"
      :progress="progress"
    ></attachment-thumbnail-progress>
    <template v-else-if="isSuccess || isShowMode">
      <div class="thumbnail-container" v-if="isThumb">
        <img
          alt="thumbnail"
          style="height: 100%; width: 100%; object-fit: cover"
          :src="thumbnailUrl"
        />
      </div>
      <div :style="fileExtensionStyle" v-else>
        {{ fileTypeExtension.toUpperCase() }}
      </div>
    </template>
    <template v-else-if="isError">
      <ErrorPopup v-slot:default="slotProps" :message="error">
        <div
          v-on="slotProps.on"
          v-bind="slotProps.attrs"
          :style="getErrorStyle"
        >
          <v-icon color="white">{{ getErrorIcon }}</v-icon>
        </div>
      </ErrorPopup>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import THEME from "constants/theme"
import AttachmentThumbnailProgress from "./AttachmentThumbnailProgress.vue"
import { mdiCloseThick } from "@mdi/js"
import ErrorPopup from "features/common/ui/ErrorPopup.vue"

const primaryColor = THEME.colors.light.primary
const textColor = THEME.colors.light.textColor
const successColor = THEME.colors.light.highlight
const errorColor = THEME.colors.light.error
const textFont = THEME.typography.text
const boldFontWeight = THEME.typography.fontWeightBold
export default defineComponent({
  name: "AttachmentThumbnail",
  components: { ErrorPopup, AttachmentThumbnailProgress },
  props: {
    fileTypeExtension: {
      type: String,
    },
    thumbnailUrl: {
      type: String,
    },
    progress: {
      type: Number, // between 0 and 1!
    },
    error: {
      type: String,
    },
  },
  computed: {
    isError(): boolean {
      return this.error != null
    },
    isProgress(): boolean {
      return (
        this.progress != null &&
        this.progress < 1 &&
        this.progress >= 0 &&
        !this.isError
      )
    },
    isSuccess(): boolean {
      return this.progress != null && this.progress >= 1 && !this.isError
    },
    isThumb(): boolean {
      return this.thumbnailUrl != null
    },
    isShowMode(): boolean {
      return !this.progress && !this.isError
    },
    getErrorIcon(): string {
      return mdiCloseThick
    },
    getBorderColor(): string {
      if (this.isShowMode) {
        return textColor
      }
      if (this.isError) {
        return errorColor
      }
      if (this.isProgress) {
        return primaryColor
      }
      if (this.isSuccess) {
        return successColor
      }
      return textColor
    },
    styleObject(): object {
      return {
        border: `2px solid ${this.getBorderColor}`,
        borderRadius: "4px",
        height: "50px",
        width: "50px",
      }
    },
    fileExtensionStyle(): object {
      return {
        color: this.isShowMode ? textColor : successColor,
        ...textFont,
        fontWeight: boldFontWeight,
        height: "100%",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }
    },
    getErrorStyle(): object {
      return {
        color: "white",
        backgroundColor: errorColor,
        height: "100%",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "help",
      }
    },
  },
})
</script>

<style>
.thumbnail-container {
  height: 100%;
}
</style>
